/**
 * Hermes Chip - Interactive chip component with module colors
 *
 * Features:
 * - Module color variants
 * - Smooth hover states with elevation
 * - Optional delete action with animation
 * - Framer Motion micro-interactions
 * - Avatar/icon support
 */

import React from 'react';
import { Chip as MuiChip, ChipProps as MuiChipProps, Avatar } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import CloseIcon from '@mui/icons-material/Close';

export type ChipModuleVariant =
  | 'courier'
  | 'santa'
  | 'email'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'default';

export interface HermesChipProps extends Omit<MuiChipProps, 'color' | 'icon' | 'avatar'> {
  /**
   * Module color variant
   * @default 'default'
   */
  moduleVariant?: ChipModuleVariant;

  /**
   * Whether chip is clickable
   * @default false
   */
  clickable?: boolean;

  /**
   * Whether chip can be deleted
   * @default false
   */
  deletable?: boolean;

  /**
   * Delete handler
   */
  onDelete?: () => void;

  /**
   * Click handler
   */
  onClick?: () => void;

  /**
   * Icon to display at start
   */
  icon?: React.ReactNode;

  /**
   * Avatar to display at start
   */
  avatar?: React.ReactNode;

  /**
   * Chip label
   */
  label: React.ReactNode;

  /**
   * Chip size
   * @default 'medium'
   */
  size?: 'small' | 'medium';

  /**
   * Chip variant style
   * @default 'filled'
   */
  variant?: 'filled' | 'outlined';
}

// Styled chip with module colors
const StyledChip = styled(MuiChip, {
  shouldForwardProp: (prop) => prop !== 'moduleVariant',
})<{ moduleVariant?: ChipModuleVariant }>(({ theme, moduleVariant, variant = 'filled' }) => {
  const getColor = () => {
    if (!moduleVariant || moduleVariant === 'default') {
      return {
        main: theme.palette.grey[500],
        light: theme.palette.grey[100],
        dark: theme.palette.grey[700],
        contrastText: theme.palette.common.white,
      };
    }

    const paletteKey = moduleVariant as keyof typeof theme.palette;
    const color = theme.palette[paletteKey] as any;
    return color || theme.palette.primary;
  };

  const color = getColor();

  return {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontWeight: 500,
    letterSpacing: '0.01em',
    borderRadius: 8,
    transition: `all ${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeInOut}`,

    // Filled variant
    ...(variant === 'filled' && {
      backgroundColor: color.main,
      color: color.contrastText || theme.palette.common.white,

      '&:hover': {
        backgroundColor: color.dark || color.main,
        boxShadow: `0 4px 12px ${color.main}40`,
      },

      '& .MuiChip-deleteIcon': {
        color: color.contrastText || theme.palette.common.white,
        opacity: 0.7,

        '&:hover': {
          opacity: 1,
          color: color.contrastText || theme.palette.common.white,
        },
      },
    }),

    // Outlined variant
    ...(variant === 'outlined' && {
      backgroundColor: 'transparent',
      borderColor: color.main,
      borderWidth: 2,
      color: color.main,

      '&:hover': {
        backgroundColor: `${color.main}15`,
        borderColor: color.dark || color.main,
        boxShadow: `0 2px 8px ${color.main}30`,
      },

      '& .MuiChip-deleteIcon': {
        color: color.main,
        opacity: 0.7,

        '&:hover': {
          opacity: 1,
          color: color.dark || color.main,
        },
      },
    }),

    // Clickable state
    '&.MuiChip-clickable': {
      cursor: 'pointer',

      '&:active': {
        boxShadow: 'none',
      },
    },

    // Delete icon animation
    '& .MuiChip-deleteIcon': {
      transition: `all ${theme.transitions.duration.shortest}ms ${theme.transitions.easing.easeInOut}`,
      margin: 0,
      marginLeft: theme.spacing(0.5),
      marginRight: theme.spacing(-0.5),
      fontSize: '1.25rem',
    },

    // Icon styling
    '& .MuiChip-icon': {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(-0.5),
    },

    // Avatar styling
    '& .MuiChip-avatar': {
      marginLeft: theme.spacing(0.5),
      marginRight: theme.spacing(-0.5),
    },
  };
});

// Delete icon wrapper with animation
const DeleteIconWrapper = styled(motion.div)({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});

// Animation variants
const chipVariants = {
  initial: {
    opacity: 0,
    scale: 0.8,
    y: -10,
  },
  animate: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: 'spring' as const,
      stiffness: 500,
      damping: 30,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    x: 20,
    transition: {
      duration: 0.2,
    },
  },
  hover: {
    scale: 1.05,
    y: -2,
    transition: {
      type: 'spring' as const,
      stiffness: 400,
      damping: 17,
    },
  },
  tap: {
    scale: 0.95,
  },
};

const deleteIconVariants = {
  rest: { scale: 1, rotate: 0 },
  hover: {
    scale: 1.2,
    rotate: 90,
    transition: {
      type: 'spring' as const,
      stiffness: 400,
      damping: 17,
    },
  },
  tap: { scale: 0.9, rotate: 90 },
};

const noMotionVariants = {
  initial: {},
  animate: {},
  exit: {},
  hover: {},
  tap: {},
};

export const Chip: React.FC<HermesChipProps> = ({
  moduleVariant = 'default',
  clickable = false,
  deletable = false,
  onDelete,
  onClick,
  icon,
  avatar,
  label,
  size = 'medium',
  variant = 'filled',
  ...props
}) => {
  const isInteractive = clickable || Boolean(onClick);

  // Check for reduced motion preference
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const variants = prefersReducedMotion ? noMotionVariants : chipVariants;

  // Custom delete icon with animation
  const deleteIcon = deletable ? (
    <DeleteIconWrapper
      initial="rest"
      whileHover="hover"
      whileTap="tap"
      variants={prefersReducedMotion ? {} : deleteIconVariants}
    >
      <CloseIcon fontSize={size === 'small' ? 'small' : 'medium'} />
    </DeleteIconWrapper>
  ) : undefined;

  return (
    <motion.div
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
      whileHover={isInteractive ? 'hover' : undefined}
      whileTap={isInteractive ? 'tap' : undefined}
      style={{ display: 'inline-block' }}
    >
      <StyledChip
        moduleVariant={moduleVariant}
        clickable={isInteractive}
        onDelete={deletable ? onDelete : undefined}
        deleteIcon={deleteIcon}
        onClick={onClick}
        icon={icon as any}
        avatar={avatar as any}
        label={label}
        size={size}
        variant={variant}
        {...props}
      />
    </motion.div>
  );
};

Chip.displayName = 'HermesChip';

export default Chip;
